/**
 * Utility functions for generating thumbnails from media files
 * Based on Element Web's implementation for Matrix compatibility
 */

// Constants based on Element Web
const MAX_WIDTH = 800;
const MAX_HEIGHT = 600;
const IMAGE_SIZE_THRESHOLD_THUMBNAIL = 1 << 15; // 32KB
const IMAGE_THUMBNAIL_MIN_REDUCTION_SIZE = 1 << 16; // 1MB
const IMAGE_THUMBNAIL_MIN_REDUCTION_PERCENT = 0.1; // 10%

// Always include thumbnail for these formats (compatibility)
const ALWAYS_INCLUDE_THUMBNAIL = ['image/avif', 'image/webp'];

/**
 * Create a thumbnail for an image or video element
 * Based on Element Web's createThumbnail function
 * @param {HTMLImageElement|HTMLVideoElement} element - The media element
 * @param {number} inputWidth - Original width
 * @param {number} inputHeight - Original height  
 * @param {string} mimeType - Output thumbnail MIME type
 * @returns {Promise<{info: object, thumbnail: Blob}>}
 */
export const createThumbnail = async (element, inputWidth, inputHeight, mimeType = 'image/jpeg') => {
  // Calculate target dimensions maintaining aspect ratio
  let targetWidth = inputWidth;
  let targetHeight = inputHeight;
  
  if (targetHeight > MAX_HEIGHT) {
    targetWidth = Math.floor(targetWidth * (MAX_HEIGHT / targetHeight));
    targetHeight = MAX_HEIGHT;
  }
  if (targetWidth > MAX_WIDTH) {
    targetHeight = Math.floor(targetHeight * (MAX_WIDTH / targetWidth));
    targetWidth = MAX_WIDTH;
  }

  // Create canvas (with OffscreenCanvas fallback)
  let canvas;
  let context;
  
  try {
    canvas = new OffscreenCanvas(targetWidth, targetHeight);
    context = canvas.getContext('2d');
  } catch {
    canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    context = canvas.getContext('2d');
  }

  // Draw and convert to blob
  context.drawImage(element, 0, 0, targetWidth, targetHeight);
  
  let thumbnail;
  if (canvas instanceof OffscreenCanvas) {
    thumbnail = await canvas.convertToBlob({ type: mimeType });
  } else {
    thumbnail = await new Promise((resolve) =>
      canvas.toBlob(resolve, mimeType),
    );
  }

  return {
    info: {
      thumbnail_info: {
        w: targetWidth,
        h: targetHeight,
        mimetype: thumbnail.type,
        size: thumbnail.size,
      },
      w: inputWidth,
      h: inputHeight,
    },
    thumbnail,
  };
};

/**
 * Determines if a thumbnail should be generated for the given file
 * @param {File} file - The file to check
 * @returns {boolean}
 */
export const shouldGenerateThumbnail = (file) => {
  // Always generate for compatibility formats
  if (ALWAYS_INCLUDE_THUMBNAIL.includes(file.type)) {
    return true;
  }
  
  // Generate if file is larger than threshold
  if (file.size >= IMAGE_SIZE_THRESHOLD_THUMBNAIL) {
    return true;
  }
  
  // For images/videos, generate by default
  return file.type.startsWith('image/') || file.type.startsWith('video/');
};

/**
 * Load image into HTMLImageElement with proper error handling
 * @param {File} imageFile - The image file to load
 * @returns {Promise<{width: number, height: number, img: HTMLImageElement}>}
 */
export const loadImageElement = (imageFile) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(imageFile);
    
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
        img,
      });
    };
    
    img.onerror = (error) => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Failed to load image: ${error.message || 'Unknown error'}`));
    };
    
    img.src = objectUrl;
  });
};

/**
 * Load video into HTMLVideoElement with proper error handling
 * @param {File} videoFile - The video file to load
 * @returns {Promise<HTMLVideoElement>}
 */
export const loadVideoElement = (videoFile) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(videoFile);
    
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(video);
    };
    
    video.onerror = (error) => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Failed to load video: ${error.message || 'Unknown error'}`));
    };
    
    video.preload = 'metadata';
    video.src = objectUrl;
  });
};

/**
 * Generates a thumbnail for images and videos using Element Web's approach
 * @param {File} file - The media file to generate a thumbnail for
 * @param {number} maxWidth - Maximum width for the thumbnail (default: 800, from Element Web)
 * @param {number} maxHeight - Maximum height for the thumbnail (default: 600, from Element Web)
 * @param {string} mimeType - Output MIME type (default: 'image/jpeg')
 * @returns {Promise<{thumbnail: Blob, info: object}|null>} Promise that resolves to thumbnail data or null
 */
export const generateThumbnail = async (file, _maxWidth = MAX_WIDTH, _maxHeight = MAX_HEIGHT, mimeType = 'image/jpeg') => {
  try {
    if (!shouldGenerateThumbnail(file)) {
      return null;
    }

    if (file.type.startsWith('image/')) {
      const { width, height, img } = await loadImageElement(file);
      
      // For large files (>= 1MB), check if thumbnail provides meaningful reduction
      if (file.size >= IMAGE_THUMBNAIL_MIN_REDUCTION_SIZE) {
        const result = await createThumbnail(img, width, height, mimeType);
        
        // Only return thumbnail if it provides meaningful reduction (>= 10%)
        const reductionPercent = (file.size - result.thumbnail.size) / file.size;
        if (reductionPercent >= IMAGE_THUMBNAIL_MIN_REDUCTION_PERCENT) {
          return result;
        }
        // If no meaningful reduction, fall through to generate anyway
      }
      
      // Generate thumbnail for all other cases:
      // - Compatibility formats (avif, webp)
      // - Files >= 32KB threshold
      // - Small files that need a thumbnail for display consistency
      return await createThumbnail(img, width, height, mimeType);
    } else if (file.type.startsWith('video/')) {
      const video = await loadVideoElement(file);
      // Set video time to 1 second or 10% of duration, whichever is smaller
      video.currentTime = Math.min(1, video.duration * 0.1);
      
      // Wait for seek to complete
      await new Promise((resolve) => {
        video.onseeked = resolve;
      });
      
      return await createThumbnail(video, video.videoWidth, video.videoHeight, mimeType);
    }
    
    return null;
  } catch {
    // Continue without thumbnail - this is not critical
    return null;
  }
};

/**
 * Gets media dimensions for images and videos using the improved utility functions
 * @param {File} file - The media file to analyze
 * @returns {Promise<{w?: number, h?: number, duration?: number}>} Promise that resolves to media dimensions
 */
export const getMediaDimensions = async (file) => {
  try {
    if (file.type.startsWith('image/')) {
      const { width, height } = await loadImageElement(file);
      return { w: width, h: height };
    } else if (file.type.startsWith('video/')) {
      const video = await loadVideoElement(file);
      return {
        w: video.videoWidth,
        h: video.videoHeight,
        duration: Math.round(video.duration * 1000), // Convert to milliseconds
      };
    } else {
      return { w: undefined, h: undefined };
    }
  } catch {
    return { w: undefined, h: undefined };
  }
};